import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  getAppointmentDetail, getCallDetail, getEvaluationByCallId,
  type DayRdv, type CallDetail, type CallEvaluation,
} from '../../lib/refonteApi';
import { useAuth } from '../../components/AuthProvider';
import RdvDetail, { EvalBlock } from '../../components/refonte/RdvDetail';
import AudioPlayer from '../../components/refonte/AudioPlayer';
import ViewAsClientToggle from '../../components/refonte/ViewAsClientToggle';
import { Skeleton } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

const fmtDur = (s?: number | null) => (s && s > 0 ? `${Math.floor(s / 60)} min ${s % 60}s` : null);

// Le champ `description` (généré) est un texte multi-sections préfixées d'un émoji :
// 🗣️ Déroulé de l'échange · 💡 Besoins exprimés · ⚠️ Points d'attention · 🧭 Conseils pour le RDV.
// On le découpe en sections propres. Le `sdr_comments` brut (verbatim) n'est PAS affiché.
type Section = { title: string; body: string };
function parseSections(text?: string | null): Section[] {
  if (!text) return [];
  const lines = text.replace(/\r/g, '').split('\n');
  const isHeader = (l: string) => /^\s*\p{Extended_Pictographic}/u.test(l);
  const stripEmoji = (l: string) => l.replace(/^\s*\p{Extended_Pictographic}[️‍\p{Extended_Pictographic}]*\s*/u, '').trim();
  const out: Section[] = [];
  let cur: Section | null = null;
  for (const line of lines) {
    if (isHeader(line)) {
      if (cur) out.push(cur);
      cur = { title: stripEmoji(line), body: '' };
    } else if (cur) {
      cur.body += (cur.body ? '\n' : '') + line;
    } else if (line.trim()) {
      cur = { title: '', body: line };
    }
  }
  if (cur) out.push(cur);
  return out.map(s => ({ title: s.title, body: s.body.trim() })).filter(s => s.body);
}

// Habillage d'une section : libellé client + icône + accent pour les conseils.
const sectionMeta = (title: string): { label: string; icon: string; accent: boolean } => {
  const t = title.toLowerCase();
  if (t.includes('déroul') || t.includes('deroul') || t.includes('échange') || t.includes('echange'))
    return { label: 'Comment le rendez-vous a été pris', icon: '🗣️', accent: false };
  if (t.includes('besoin')) return { label: 'Besoins exprimés', icon: '💡', accent: false };
  if (t.includes('attention')) return { label: "Points d'attention", icon: '⚠️', accent: false };
  if (t.includes('conseil')) return { label: 'Conseils pour le rendez-vous', icon: '🧭', accent: true };
  return { label: title || 'Note', icon: '📝', accent: false };
};

const SectionCard: React.FC<{ section: Section }> = ({ section }) => {
  const m = sectionMeta(section.title);
  return (
    <div className={`rounded-card border p-4 ${m.accent ? 'bg-hook-rose/5 border-hook-rose/30' : 'bg-panel border-line'}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[15px]">{m.icon}</span>
        <span className={`font-mono text-[10px] uppercase tracking-widest ${m.accent ? 'text-hook-rose' : 'text-faint'}`}>{m.label}</span>
      </div>
      <p className="text-[12.5px] text-dim leading-relaxed whitespace-pre-wrap">{section.body}</p>
    </div>
  );
};

// Fiche détail RDV — page client. Colonne fiche + actions (tout le monde peut statuer),
// résumé de l'appel, puis « Préparer ce rendez-vous » = les sections générées (déroulé,
// besoins, points d'attention, conseils). Pas de journal, pas de verbatim SDR brut.
const PortalRdvPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { profile } = useAuth();
  const role = profile?.role || 'client';
  const trueClient = role === 'client';
  const isInternal = !trueClient;
  const viewAsClient = isInternal && params.get('asclient') === '1';
  const asClient = trueClient || viewAsClient;         // rend-on comme un client ?
  const effRole = asClient ? 'client' : role;          // rôle effectif pour l'affichage
  const setViewAsClient = (v: boolean) =>
    setParams(prev => { const n = new URLSearchParams(prev); if (v) n.set('asclient', '1'); else n.delete('asclient'); return n; });

  const [rdv, setRdv] = useState<DayRdv | null>(null);
  const [call, setCall] = useState<CallDetail | null>(null);
  const [evalRes, setEvalRes] = useState<CallEvaluation | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true); setErr(null);
    try {
      const d = await getAppointmentDetail(id);
      setRdv(d);
      setCall(d?.call_id ? await getCallDetail(d.call_id) : null);
      // Évaluation IA de l'appel — interne uniquement (jamais côté client).
      setEvalRes(d?.call_id && !asClient ? await getEvaluationByCallId(d.call_id).catch(() => null) : null);
    } catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, [id, asClient]);
  useEffect(() => { load(); }, [load]);

  const sections = parseSections(rdv?.description);

  return (
    <div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button onClick={() => navigate(-1)} className="text-[12px] font-semibold text-dim hover:text-ink">← Retour</button>
        {isInternal && <ViewAsClientToggle on={viewAsClient} onChange={setViewAsClient} />}
      </div>
      <h1 className="text-[21px] font-bold tracking-tight mt-2 mb-4">Détail du rendez-vous</h1>

      {err && <EmptyState tone="error" title="Chargement impossible" hint={err} className="mb-4" />}
      {loading ? <Skeleton className="h-[70vh] rounded-card" /> : !rdv ? (
        <EmptyState title="RDV introuvable" hint="Ce rendez-vous n'existe pas ou n'est pas accessible." />
      ) : (
        <>
          {/* Haut : fiche + actions | résumé de l'appel (colonnes à hauteur égale, pas de trou) */}
          <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-4 items-stretch mb-4">
            <div className="bg-panel border border-line rounded-card p-5">
              {/* hideNote/hideEval : verbatim SDR et éval jamais dans la fiche ; l'éval a son cadre en bas.
                  effRole : en preview « vue client », on rend exactement comme un client. */}
              <RdvDetail rdv={rdv} role={effRole} onChanged={load} bookingFirst hideNote hideEval hideContract={asClient} previewAsClient={viewAsClient} />
            </div>

            {/* Les RDV antérieurs à juin 2026 portent des call_id d'anciens identifiants Minari
                (1 561 orphelins) : pas de résumé ni d'audio récupérables → on masque le bloc
                plutôt que d'afficher « indisponible » en série (audit 08/10). */}
            {!(rdv.date && rdv.date < '2026-06-01' && !call?.call_summary && !call?.recording_url) && (
            <div className="bg-panel border border-line rounded-card p-5 flex flex-col">
              <div className="font-mono text-[10px] uppercase tracking-widest text-faint mb-2">
                Résumé de la conversation {fmtDur(call?.duration_seconds) && <span className="text-dim">· {fmtDur(call?.duration_seconds)}</span>}
              </div>
              {!rdv.call_id ? (
                <p className="text-[12px] text-faint">Aucune conversation rattachée à ce rendez-vous.</p>
              ) : call?.call_summary ? (
                <p className="text-[12.5px] text-dim bg-panel-2 border border-line rounded-lg p-3 whitespace-pre-wrap leading-relaxed flex-1">{call.call_summary}</p>
              ) : (
                <p className="text-[12px] text-faint flex-1">Résumé de conversation indisponible.</p>
              )}
              {call?.recording_url
                ? <AudioPlayer src={call.recording_url} className="mt-3" />
                : rdv.call_id && <p className="text-[11px] text-faint mt-3">🔇 Enregistrement indisponible pour cette conversation.</p>}
            </div>
            )}
          </div>


          {/* Préparer ce rendez-vous : sections générées (déroulé / besoins / points d'attention / conseils) */}
          {sections.length > 0 && (
            <div className="bg-panel border border-line rounded-card p-5">
              <h2 className="text-[15px] font-bold mb-1">Préparer ce rendez-vous</h2>
              <p className="text-[12px] text-dim mb-4">Le contexte de l'échange et nos conseils pour aborder ce prospect.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {sections.map((s, i) => <SectionCard key={i} section={s} />)}
              </div>
            </div>
          )}

          {/* Évaluation de la conversation — cadre dédié, tout en bas, INTERNE uniquement (masqué au client) */}
          {!asClient && rdv.call_id && (
            <div className="mt-4">
              <h2 className="text-[15px] font-bold mb-1">Évaluation de la conversation · interne</h2>
              <p className="text-[12px] text-dim mb-3">Analyse IA de la prise de rendez-vous — non visible par le client.</p>
              {evalRes
                ? <EvalBlock ev={evalRes} />
                : <div className="rounded-card border border-line-2 bg-raise/40 p-4 text-[12px] text-faint">Pas d'évaluation IA disponible pour cette conversation.</div>}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PortalRdvPage;
